import { z } from 'zod'

import { GetBillCategorySchema } from './bill-category'
import { XeroInvoiceStatusEnum, XeroTaxTypeEnum } from './invoice'
import { GetPaymentTagSchema } from './payment-tag'

export const BillTypeEnum = z.enum(['invoice', 'bill'] as const)

export const GetBillCompanySchema = z.object({
  id: z.number(),
  organisation_id: z.number().nullish(),
  title: z.string(),
})

export const GetBillOrganisationSchema = z.object({
  id: z.number(),
  name: z.string().nullish(),
})

export const BillCounterpartyRoleSchema = z.enum([
  'carrier',
  'customer',
  'end_carrier',
  'end_customer',
  'other',
])

export const GetBillItemSchema = z.object({
  amount_base: z.number().nullable(),
  amount_base_decimal: z.number().nullable(),
  base_currency: z.string().nullable(),
  currency: z.string(),
  description: z.string(),
  id: z.number(),
  line_amount: z.number(),
  line_amount_decimal: z.number(),
  quantity: z.string().nullable(),
  tax_amount: z.number(),
  tax_type: XeroTaxTypeEnum.nullable(),
  xero_line_item_id: z.string().nullable(),
})

export const PaymentMethodSchema = z.enum(['regular_transfer', 'cx_smart', 'proforma'])

export const PaymentStatusSchema = z.enum(['unpaid', 'paid'])

export const GetEstimatedCostSchema = z.object({
  amount: z.number(),
  amount_base: z.number().nullable(),
  amount_base_decimal: z.number().nullable(),
  amount_decimal: z.number(),
  base_currency: z.string().nullable(),
  category: GetBillCategorySchema.nullable(),
  created_at: z.coerce.date(),
  currency: z.string(),
  exchange_date: z.string().nullable(),
  exchange_rate: z.string().nullable(),
  id: z.number(),
  is_carrier_rate: z.boolean().default(false),
  is_estimated: z.literal(true),
  linked_bill_id: z.number().nullable(),
  load_id: z.number(),
  notes: z.string().nullable(),
  payment_method: PaymentMethodSchema.nullish(),
  payment_status: PaymentStatusSchema.nullish(),
  payment_tags: GetPaymentTagSchema.array().optional(),
  planned_payment_date: z.string().nullish(),
  updated_at: z.coerce.date(),
})

export const GetBillSchema = z.object({
  amount: z.number(),
  amount_base: z.number().nullable(),
  amount_base_decimal: z.number().nullable(),
  amount_decimal: z.number(),
  base_currency: z.string().nullable(),
  category: GetBillCategorySchema.nullable(),
  company: GetBillCompanySchema.nullish().transform(value => value ?? null),
  counterparty_role: BillCounterpartyRoleSchema.nullish().transform(value => value ?? null),
  created_at: z.coerce.date(),
  currency: z.string(),
  due_date: z.string().nullable(),
  estimated_cost: GetEstimatedCostSchema.nullable(),
  id: z.number(),
  items: GetBillItemSchema.array(),
  load_id: z.number(),
  notes: z.string().nullable(),
  organisation: GetBillOrganisationSchema.nullish().transform(value => value ?? null),
  payment_method: PaymentMethodSchema.nullish(),
  payment_tags: GetPaymentTagSchema.array().optional(),
  planned_payment_date: z.string().nullish(),
  status: XeroInvoiceStatusEnum.nullable(),
  supplier_name: z.string().nullable(),
  type: BillTypeEnum.nullable(),
  updated_at: z.coerce.date(),
  xero_contact_id: z.string().nullable(),
  xero_deep_link: z.string().nullable(),
  xero_invoice_id: z.string().nullable(),
  xero_invoice_number: z.string().nullable(),
})

export const GetBillPreviewItemSchema = z.object({
  already_used: z.boolean(),
  amount_base: z.number().nullable(),
  amount_base_decimal: z.number().nullable(),
  base_currency: z.string().nullable(),
  currency: z
    .string()
    .nullish()
    .transform(value => value ?? null),
  description: z.string(),
  line_amount: z.number(),
  line_amount_decimal: z.number(),
  quantity: z.number().nullish(),
  xero_line_item_id: z.string(),
})

export const GetBillPreviewSchema = z.object({
  already_used: z.boolean(),
  base_currency: z
    .string()
    .nullish()
    .transform(value => value ?? null),
  company: GetBillCompanySchema.nullish().transform(value => value ?? null),
  counterparty_role: BillCounterpartyRoleSchema.nullish().transform(value => value ?? null),
  currency: z
    .string()
    .nullish()
    .transform(value => value ?? null),
  due_date: z
    .string()
    .nullish()
    .transform(value => value ?? null),
  items: GetBillPreviewItemSchema.array(),
  organisation: GetBillOrganisationSchema.nullish().transform(value => value ?? null),
  status: z.string().nullable(),
  supplier_name: z
    .string()
    .nullish()
    .transform(value => value ?? null),
  total: z.number(),
  total_base: z.number(),
  total_base_decimal: z.number(),
  total_decimal: z.number(),
  xero_contact_id: z.string().nullable(),
  xero_invoice_id: z.string().nullable(),
  xero_invoice_number: z.string().nullable(),
})

export const GetBillsTotalSchema = z.object({
  bills_total: z.number(),
  bills_total_decimal: z.number(),
  currency: z.string(),
  estimated_costs_total: z.number(),
  estimated_costs_total_decimal: z.number(),
  total: z.number(),
  total_decimal: z.number(),
})

export const EstimatedCostComparisonSchema = z.object({
  bill: z.object({
    base_currency: z.string().nullable(),
    currency: z.string().nullable(),
    selected_total: z.number(),
    selected_total_base: z.number(),
    selected_total_decimal: z.number(),
  }),
  comparison_currency: z.string().nullable(),
  difference: z.number(),
  difference_decimal: z.number(),
  estimate: z.object({
    amount: z.number(),
    amount_base: z.number().nullable(),
    amount_decimal: z.number(),
    base_currency: z.string().nullable(),
    currency: z.string().nullable(),
  }),
  matches: z.boolean(),
})

export type EstimatedCostComparison = z.infer<typeof EstimatedCostComparisonSchema>
export type GetBill = z.infer<typeof GetBillSchema>
export type GetBillItem = z.infer<typeof GetBillItemSchema>
export type GetBillPreview = z.infer<typeof GetBillPreviewSchema>
export type GetBillPreviewItem = z.infer<typeof GetBillPreviewItemSchema>
export type GetBillsTotal = z.infer<typeof GetBillsTotalSchema>
export type GetEstimatedCost = z.infer<typeof GetEstimatedCostSchema>
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>
